import React, { useState, useEffect } from "react";
import { getFields, saveFields, getData } from "./_request";
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

const NUM_FIELDS = 30;

const ClassForms = () => {
  const [activeTab, setActiveTab] = useState("data");
  const [fields, setFields] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldsError, setFieldsError] = useState(null);
  const [fieldEdits, setFieldEdits] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);

  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  // Fetch field labels
  useEffect(() => {
    setFieldsLoading(true);
    getFields()
      .then(res => {
        setFields(res.fields || {});
        setFieldEdits(res.fields || {});
        setFieldsError(null);
      })
      .catch(err => setFieldsError("Failed to load fields"))
      .finally(() => setFieldsLoading(false));
  }, []);

  // Fetch data
  useEffect(() => {
    setDataLoading(true);
    getData()
      .then(res => {
        setData(res.data || []);
        setDataError(null);
      })
      .catch(err => setDataError("Failed to load data"))
      .finally(() => setDataLoading(false));
  }, []);

  // Handle field label change
  const handleFieldEdit = (key, value) => {
    setFieldEdits(prev => ({ ...prev, [key]: value }));
  };

  // Save field labels
  const handleSaveFields = async () => {
    setSaveStatus(null);
    try {
      const res = await saveFields(fieldEdits);
      if (res.success) {
        setFields(fieldEdits);
        setSaveStatus("Saved successfully!");
      } else {
        setSaveStatus("Save failed: " + (res.message || "Unknown error"));
      }
    } catch (err) {
      setSaveStatus("Save failed: " + err.message);
    }
  };

  // Get only labeled fields
  const labeledFields = [];
  for (let i = 1; i <= NUM_FIELDS; i++) {
    if (fields[`udf${i}_label`]) {
      labeledFields.push({
        key: `udf${i}`,
        label: fields[`udf${i}_label`],
      });
    }
  }

  return (
    <div className="min-h-screen py-8 px-2">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg p-8">
        <h1 style={{ fontWeight: 700, fontSize: '2rem', color: '#222', marginBottom: '2rem' }}>Class Forms</h1>
        {/* Tabs */}
        <div className="flex space-x-2 mb-8">
          <Button
            variant={activeTab === "data" ? "contained" : "outlined"}
            onClick={() => setActiveTab("data")}
            className="px-4 py-2 rounded-md font-medium"
          >
            Data
          </Button>
          <Button
            variant={activeTab === "fields" ? "contained" : "outlined"}
            onClick={() => setActiveTab("fields")}
            className="px-4 py-2 rounded-md font-medium"
          >
            Form Fields
          </Button>
        </div>
        {/* Tab Content */}
        {activeTab === "data" ? (
          <div>
            {dataLoading || fieldsLoading ? (
              <div style={{ textAlign: 'center', padding: '16px' }}><CircularProgress /></div>
            ) : dataError ? (
              <div className="text-red-500 py-10 text-center">{dataError}</div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="w-full border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-100">
                      {labeledFields.map(f => (
                        <th key={f.key} className="border border-gray-300 px-4 py-2 text-primary text-base">{f.label}</th>
                      ))}
                      <th className="border border-gray-300 px-4 py-2 text-primary text-base">Submitted At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr><td colSpan={labeledFields.length + 1} className="text-center py-8 text-gray-500">No submissions yet.</td></tr>
                    ) : data.map((row, idx) => (
                      <tr key={row.id || idx} className="hover:bg-gray-50">
                        {labeledFields.map(f => (
                          <td key={f.key} className="border border-gray-300 px-4 py-2">{row[f.key]}</td>
                        ))}
                        <td className="border border-gray-300 px-4 py-2">{row.submitted_at ? new Date(row.submitted_at).toLocaleString() : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div>
            {fieldsLoading ? (
              <div style={{ textAlign: 'center', padding: '16px' }}><CircularProgress /></div>
            ) : fieldsError ? (
              <div className="text-red-500 py-10 text-center">{fieldsError}</div>
            ) : (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  handleSaveFields();
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(NUM_FIELDS)].map((_, i) => (
                    <div key={`udf${i + 1}`} className="flex items-center gap-2 bg-gray-50 rounded p-2">
                      <label className="w-24 text-gray-700 font-medium">UDF{i + 1}:</label>
                      <input
                        type="text"
                        className="border rounded px-3 py-2 flex-1 focus:ring focus:ring-primary focus:outline-none"
                        value={fieldEdits[`udf${i + 1}_label`] || ""}
                        onChange={e => handleFieldEdit(`udf${i + 1}_label`, e.target.value)}
                        placeholder={`Label for UDF${i + 1}`}
                      />
                    </div>
                  ))}
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={fieldsLoading}
                >
                  {fieldsLoading ? <CircularProgress size={20} color="inherit" /> : 'Update'}
                </Button>
                {saveStatus && <div className="mt-2 text-sm text-green-600">{saveStatus}</div>}
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassForms; 